import React, { useState, useEffect } from 'react'
import Breadcrumb from '../../components/common/Breadcrumb'
import { IconLoader2, IconEdit, IconCheck, IconX, IconLock, IconEye, IconEyeOff } from '@tabler/icons-react'
import toast from 'react-hot-toast'
import { useMyProfile, useUpdateMyProfile, useChangePassword } from '../../hooks/useEmployees'
import { useAuth } from '../../contexts/AuthContext'

const EDITABLE_FIELDS = [
    { key: 'first_name', label: 'First Name' },
    { key: 'middle_name', label: 'Middle Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'address', label: 'Address' },
    { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
]

const READ_ONLY_FIELDS = [
    { key: 'employee_no', label: 'Employee No.' },
    { key: 'work_email', label: 'Work Email' },
    { key: 'job_title', label: 'Job Title' },
    { key: 'grade_level', label: 'Grade Level' },
    { key: 'hire_date', label: 'Hire Date' },
    { key: 'employment_status', label: 'Employment Status' },
    { key: 'gender', label: 'Gender' },
    { key: 'department_name', label: 'Department' },
    { key: 'primary_unit_name', label: 'Unit' },
    { key: 'line_manager_name', label: 'Line Manager' },
]

export default function Profile() {
    const { logout } = useAuth()
    const { data: profile, isLoading } = useMyProfile()
    const updateProfile = useUpdateMyProfile()
    const changePassword = useChangePassword()

    const [editing, setEditing] = useState(false)
    const [formData, setFormData] = useState({})

    const [pwData, setPwData] = useState({ old_password: '', new_password: '', confirm_password: '' })
    const [pwErrors, setPwErrors] = useState({})
    const [showOld, setShowOld] = useState(false)
    const [showNew, setShowNew] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)

    useEffect(() => {
        if (profile) {
            const initial = {}
            EDITABLE_FIELDS.forEach(f => { initial[f.key] = profile[f.key] || '' })
            setFormData(initial)
        }
    }, [profile])

    const handleSave = async () => {
        try {
            await updateProfile.mutateAsync(formData)
            toast.success('Profile updated successfully')
            setEditing(false)
        } catch {
            // interceptor handles error toast
        }
    }

    const handleCancel = () => {
        const reset = {}
        EDITABLE_FIELDS.forEach(f => { reset[f.key] = profile?.[f.key] || '' })
        setFormData(reset)
        setEditing(false)
    }

    const handleChangePassword = async () => {
        const errs = {}
        if (!pwData.old_password) errs.old_password = 'Current password is required'
        if (!pwData.new_password || pwData.new_password.length < 8) errs.new_password = 'New password must be at least 8 characters'
        if (!pwData.confirm_password) errs.confirm_password = 'Please confirm new password'
        else if (pwData.new_password !== pwData.confirm_password) errs.confirm_password = 'Passwords do not match'

        if (Object.keys(errs).length > 0) {
            setPwErrors(errs)
            return
        }

        try {
            await changePassword.mutateAsync({
                old_password: pwData.old_password,
                new_password: pwData.new_password,
                confirm_password: pwData.confirm_password,
            })
            toast.success('Password changed successfully. Please log in again.')
            setPwData({ old_password: '', new_password: '', confirm_password: '' })
            setPwErrors({})
            setTimeout(() => logout(), 1500)
        } catch {
            // interceptor handles error toast
        }
    }

    return (
        <>
            <Breadcrumb title="Basic Information" path="Account Settings / Basic Information" />

            {isLoading ? (
                <div className="flex justify-center py-60">
                    <IconLoader2 size={28} className="animate-spin text-primary" />
                </div>
            ) : (
                <div className="space-y-20 p-20">
                    {/* Profile Banner */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-primary px-20 py-20 flex items-center justify-between">
                            <div className="flex items-center gap-15">
                                <div className="w-60 h-60 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-xl border-2 border-white/30">
                                    {profile?.first_name?.[0]?.toUpperCase()}{profile?.last_name?.[0]?.toUpperCase()}
                                </div>
                                <div>
                                    <p className="text-white font-bold text-lg">{profile?.full_name || '—'}</p>
                                    <p className="text-white/80 text-sm">{profile?.work_email}</p>
                                    <p className="text-white/60 text-xs mt-2">{profile?.job_title} · {profile?.department_name}</p>
                                </div>
                            </div>
                            {!editing && (
                                <button
                                    onClick={() => setEditing(true)}
                                    className="flex items-center gap-6 px-12 py-4 text-sm font-bold text-white border border-white/40 rounded-lg hover:bg-white/10 transition-colors"
                                >
                                    <IconEdit size={15} />
                                    Edit
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Personal Details */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="px-20 py-8 border-b border-border">
                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Personal Details</p>
                        </div>
                        <div className="p-20">
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-20">
                                {EDITABLE_FIELDS.map(field => (
                                    <div key={field.key}>
                                        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-6">{field.label}</label>
                                        {editing ? (
                                            <input
                                                type={field.type || 'text'}
                                                value={formData[field.key] || ''}
                                                onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                                                className="w-full text-sm border border-border rounded-lg px-12 py-8 bg-white outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                            />
                                        ) : (
                                            <p className="text-sm font-medium text-text-primary">{profile?.[field.key] || '—'}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Employment Details */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="px-20 py-8 border-b border-border">
                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Employment Details</p>
                        </div>
                        <div className="p-20">
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-20">
                                {READ_ONLY_FIELDS.map(field => (
                                    <div key={field.key}>
                                        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-6">{field.label}</label>
                                        <p className="text-sm font-medium text-text-primary capitalize">{profile?.[field.key] || '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Change Password */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="px-20 py-8 border-b border-border flex items-center gap-8">
                            <IconLock size={15} className="text-gray-400" />
                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Change Password</p>
                        </div>
                        <div className="p-20">
                            <div className="grid md:grid-cols-3 gap-20">
                                <div className="form-group">
                                    <label className="text-sm mb-6 block">Current Password</label>
                                    <div className="relative">
                                        <input
                                            type={showOld ? 'text' : 'password'}
                                            value={pwData.old_password}
                                            onChange={(e) => { setPwData(p => ({ ...p, old_password: e.target.value })); setPwErrors(p => ({ ...p, old_password: '' })) }}
                                            placeholder="Enter current password"
                                            className="pr-40"
                                        />
                                        <button type="button" onClick={() => setShowOld(p => !p)} className="absolute right-10 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-main transition-colors">
                                            {showOld ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                                        </button>
                                    </div>
                                    {pwErrors.old_password && <p className="text-danger text-xs mt-4">{pwErrors.old_password}</p>}
                                </div>
                                <div className="form-group">
                                    <label className="text-sm mb-6 block">New Password</label>
                                    <div className="relative">
                                        <input
                                            type={showNew ? 'text' : 'password'}
                                            value={pwData.new_password}
                                            onChange={(e) => { setPwData(p => ({ ...p, new_password: e.target.value })); setPwErrors(p => ({ ...p, new_password: '' })) }}
                                            placeholder="Min. 8 characters"
                                            className="pr-40"
                                        />
                                        <button type="button" onClick={() => setShowNew(p => !p)} className="absolute right-10 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-main transition-colors">
                                            {showNew ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                                        </button>
                                    </div>
                                    {pwErrors.new_password && <p className="text-danger text-xs mt-4">{pwErrors.new_password}</p>}
                                </div>
                                <div className="form-group">
                                    <label className="text-sm mb-6 block">Confirm New Password</label>
                                    <div className="relative">
                                        <input
                                            type={showConfirm ? 'text' : 'password'}
                                            value={pwData.confirm_password}
                                            onChange={(e) => { setPwData(p => ({ ...p, confirm_password: e.target.value })); setPwErrors(p => ({ ...p, confirm_password: '' })) }}
                                            placeholder="Re-enter new password"
                                            className="pr-40"
                                        />
                                        <button type="button" onClick={() => setShowConfirm(p => !p)} className="absolute right-10 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-main transition-colors">
                                            {showConfirm ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                                        </button>
                                    </div>
                                    {pwErrors.confirm_password && <p className="text-danger text-xs mt-4">{pwErrors.confirm_password}</p>}
                                </div>
                            </div>
                            <div className="flex justify-end mt-20">
                                <button
                                    onClick={handleChangePassword}
                                    disabled={changePassword.isPending}
                                    className="btn btn-primary flex items-center gap-6 disabled:opacity-50"
                                >
                                    {changePassword.isPending
                                        ? <><IconLoader2 size={14} className="animate-spin" /> Updating...</>
                                        : <><IconCheck size={14} /> Update Password</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Save/Cancel footer when editing */}
                    {editing && (
                        <div className="flex justify-end gap-10">
                            <button
                                onClick={handleCancel}
                                className="btn btn-outline-secondary flex items-center gap-6"
                            >
                                <IconX size={14} />
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={updateProfile.isPending}
                                className="btn btn-primary flex items-center gap-6 disabled:opacity-50"
                            >
                                {updateProfile.isPending
                                    ? <><IconLoader2 size={14} className="animate-spin" /> Saving...</>
                                    : <><IconCheck size={14} /> Save Changes</>
                                }
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}
